// Formats a Pup card's `modifier` string into the styled modifier box (see PupCard.js).
//
// The modifier is authored in a canonical form in the backend catalog (PupCardType.java):
//   - keyword modifiers first — Interrupt / Conditional / Zombie / Automatic / Permanent
//     (and Counter) — comma-separated, then " - ", then the stage/phase text; OR
//   - stage/phase text alone (no keyword modifiers).
//
// Display rules (from the card wording spec):
//   - keyword modifiers render ITALIC, ordered Zombie-first then alphabetical, comma-joined.
//     If stage/phase text follows, "<em>keywords</em> - stageText" (the stage text stays plain,
//     with no "Stage:" prefix).
//   - a card carrying ONLY stage/phase info gets a BOLD "Stage:" prefix instead, then the stage
//     text. That prefix is the same whether or not a phase is named — the phase already rides
//     the stage text in parentheses ("Stage: Flop (Start)"), so the older "Stage (Phase)" prefix
//     just spent a line's worth of width restating it. `hasPhase` is still parsed (it describes
//     the modifier and is asserted in the tests) but no longer selects the prefix.
//
// Behavior comes from typed metadata, never this text — so this is purely presentational.

// The non-stage/phase keyword modifiers we recognize and italicize.
const KEYWORDS = ['Interrupt', 'Conditional', 'Zombie', 'Automatic', 'Permanent', 'Counter'];
const KEYWORD_BY_LOWER = new Map(KEYWORDS.map((k) => [k.toLowerCase(), k]));
// Zombie always comes first; every other keyword is alphabetical.
const KEYWORD_ORDER = { Zombie: 0, Automatic: 1, Conditional: 2, Counter: 3, Interrupt: 4, Permanent: 5 };

const isKeyword = (token) => KEYWORD_BY_LOWER.has(token.trim().toLowerCase());
const canonicalKeyword = (token) => KEYWORD_BY_LOWER.get(token.trim().toLowerCase());
const splitTokens = (segment) => segment.split(',').map((t) => t.trim()).filter(Boolean);

// Parse a modifier string into { keywords, stageText, hasPhase }.
// Exported for unit testing; PupCard uses <ModifierText> below.
export function parseModifier(modifier) {
  const raw = (modifier || '').trim();
  if (!raw) return { keywords: [], stageText: '', hasPhase: false };

  let keywords = [];
  let stageText = '';

  const dashIdx = raw.indexOf(' - ');
  if (dashIdx !== -1) {
    // "<keywords> - <stage text>" when the left side is all keyword modifiers.
    const leftTokens = splitTokens(raw.slice(0, dashIdx));
    if (leftTokens.length && leftTokens.every(isKeyword)) {
      keywords = leftTokens.map(canonicalKeyword);
      stageText = raw.slice(dashIdx + 3).trim();
    } else {
      stageText = raw;
    }
  } else {
    const tokens = splitTokens(raw);
    if (tokens.length && tokens.every(isKeyword)) {
      keywords = tokens.map(canonicalKeyword); // keyword-only card
    } else {
      stageText = raw; // stage/phase-only card
    }
  }

  keywords.sort((a, b) => (KEYWORD_ORDER[a] ?? 99) - (KEYWORD_ORDER[b] ?? 99));
  const hasPhase = /\(\s*(?:start|decision|end)/i.test(stageText);
  return { keywords, stageText, hasPhase };
}

// Renders the parsed modifier as a fragment of <strong>/<em>/text for the modifier box.
export function ModifierText({ modifier }) {
  const { keywords, stageText } = parseModifier(modifier);

  if (!keywords.length && !stageText) return null;

  if (keywords.length) {
    return (
      <>
        <em>{keywords.join(', ')}</em>
        {stageText ? ` - ${stageText}` : null}
      </>
    );
  }

  return (
    <>
      <strong>Stage:</strong> {stageText}
    </>
  );
}
